// Iris Realty — Help Center content
// Each article is a plain data object rendered by pages/Help.jsx
// To add a new article: append to ARTICLES + reference by slug in TOC groups.

export const HELP_VERSION = "2026.02.20";

export const HELP_CATEGORIES = [
  {
    key: "start",
    label: "Getting started",
    slugs: ["overview", "workspaces", "roles-permissions", "keyboard-shortcuts"],
  },
  {
    key: "deals",
    label: "Deal sourcing",
    slugs: ["deal-radar", "deal-preferences", "live-listings", "compare-2-deals", "deal-analyzer", "auctions"],
  },
  {
    key: "portfolio",
    label: "Portfolio",
    slugs: ["properties", "boq-upload", "boq-audit", "vendors", "refurb", "purchase-orders"],
  },
  {
    key: "finance",
    label: "Finance",
    slugs: ["invoices", "accounting", "approvals-workflow"],
  },
  {
    key: "market",
    label: "Market intelligence",
    slugs: ["trends", "hotspots", "dld-data", "daily-digest", "rental-leaderboard"],
  },
  {
    key: "vendors",
    label: "Vendor portal",
    slugs: ["vendor-portal-intro", "vendor-team-management"],
  },
  {
    key: "ai",
    label: "Iris AI",
    slugs: ["iris-ai-overview"],
  },
  {
    key: "security",
    label: "Security & backups",
    slugs: ["two-fa-setup", "audit-log"],
  },
];

// Reusable section builders — keep articles concise but consistent.
const s = {
  summary: (text) => ({ type: "summary", text }),
  h2: (text) => ({ type: "h2", text }),
  h3: (text) => ({ type: "h3", text }),
  p: (text) => ({ type: "p", text }),
  bullets: (items) => ({ type: "bullets", items }),
  numbered: (items) => ({ type: "numbered", items }),
  tryLive: (href, label) => ({ type: "try_live", href, label }),
  fieldTable: (title, rows) => ({ type: "field_table", title, rows }),
  callout: (variant, text) => ({ type: "callout", variant, text }),
  faq: (qa) => ({ type: "faq", qa }),
  related: (slugs) => ({ type: "related", slugs }),
  code: (lang, text) => ({ type: "code", lang, text }),
  screenshot: (alt, src, caption) => ({ type: "screenshot", alt, src, caption }),
};

// ────────────────────────────────────────────────────────────────
// ARTICLES
// ────────────────────────────────────────────────────────────────
export const ARTICLES = {
  overview: {
    slug: "overview",
    title: "Welcome to Iris Realty",
    subtitle: "The operating system for Dubai flip investors",
    icon: "Sparkles",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris Realty combines deal sourcing, refurb management, accounting and market intelligence into one workspace. Everything is powered by Iris AI (Claude Sonnet 4.5) so you can make faster, evidence-backed decisions."),
      s.h2("The three pillars"),
      s.bullets([
        "**Deal sourcing** — Iris AI scans Bayut + PropertyFinder listings and Emirates Auction every 6 hours, ranks the best 8 deals daily, and explains the score behind each.",
        "**Portfolio** — Track every property from Sourcing → Under Offer → Owned → Refurb → Listed → Sold. Vendors, BOQs, POs, invoices and snags are all linked to a property.",
        "**Market intelligence** — Historical DLD transactions (~200K rows), live inventory, per-community hotness scores and Iris AI hotspot analysis.",
      ]),
      s.h2("Your first 5 minutes"),
      s.numbered([
        "Set your workspace — top-right badge switches between **Live** (real data) and **Demo** (seeded sample data).",
        "Complete 2FA setup at **/app/security** (workspace-enforced).",
        "Open **Deal Radar** on the Trends page — Iris AI's top 8 deals refresh every 6h.",
        "Explore a deal with **Why this score?** — full narrative + score breakdown + comparables.",
        "Add your first vendor at **/app/vendors** and create a portal login for them.",
      ]),
      s.tryLive("/app/dashboard", "Open my dashboard"),
      s.related(["workspaces", "roles-permissions", "deal-radar", "iris-ai-overview"]),
    ],
  },

  workspaces: {
    slug: "workspaces",
    title: "Workspaces — Live vs Demo",
    subtitle: "How data isolation works",
    icon: "Layers",
    read_time_min: 2,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris runs two isolated workspaces per tenant: **Live** for real operations and **Demo** for training/sandbox. Every record is tagged with a `workspace` field so switching hides/shows the appropriate data."),
      s.h2("Switch workspaces"),
      s.p("Click the workspace pill in the top-right header (next to your avatar). Your choice is persisted in `localStorage.flipn_workspace` and sent to the backend via the `X-Workspace` header."),
      s.fieldTable("What's in each workspace", [
        { name: "Live", meaning: "Real properties, vendors, invoices. Only visible to authenticated internal team." },
        { name: "Demo", meaning: "Seeded sample properties + vendors + BOQs. Reset-safe — you can experiment freely." },
      ]),
      s.callout("info", "Market Intelligence data (Bayut + PropertyFinder listings, DLD transactions) is **workspace-scoped** but only Live gets scheduled sync. Demo can be seeded on demand."),
      s.faq([
        { q: "Can vendors switch workspaces?", a: "No. Vendor portal accounts are pinned to whichever workspace they were created in." },
        { q: "Does Demo data leak into Live?", a: "Never. Every query filters by `workspace` in Mongo." },
      ]),
      s.related(["overview", "roles-permissions"]),
    ],
  },

  "roles-permissions": {
    slug: "roles-permissions",
    title: "Roles & permissions",
    subtitle: "Who can see and do what",
    icon: "Shield",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris ships with 8 internal roles + 1 external vendor role. Sensitive actions (approvals, admin settings, vendor account creation) are gated at the API layer via FastAPI `require_roles()` dependencies."),
      s.fieldTable("Internal roles", [
        { name: "owner", meaning: "Full control — user management, admin settings, financial approvals, delete access." },
        { name: "md", meaning: "Same as owner in day-to-day operations; can be scoped tighter for enterprise setups." },
        { name: "project_manager", meaning: "Full property + BOQ + vendor + refurb access. Cannot manage users or delete records." },
        { name: "accountant", meaning: "Read/write invoices, POs, cash flow. Cannot edit BOQs or property acquisition prices." },
        { name: "payments", meaning: "Executes payments once approved; read-only on approvals." },
        { name: "site_engineer", meaning: "Property + snags + drawings read/write; no financials." },
        { name: "viewer", meaning: "Read-only across all modules." },
        { name: "vendor", meaning: "External. Only sees data linked to their `vendor_id`. Uses `/vendor-portal`." },
      ]),
      s.h2("How to change a user's role"),
      s.numbered([
        "Go to **/app/team**",
        "Click the user row",
        "Pick the new role and save",
        "The user must log out and back in to refresh their session token claims",
      ]),
      s.callout("warning", "You need at least ONE owner in the workspace at all times — Iris blocks demoting the last owner."),
      s.related(["vendor-portal-intro", "vendor-team-management", "two-fa-setup"]),
    ],
  },

  "keyboard-shortcuts": {
    slug: "keyboard-shortcuts",
    title: "Keyboard shortcuts",
    subtitle: "Move faster",
    icon: "Command",
    read_time_min: 1,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Coming soon — global command palette (⌘K) and per-page shortcuts. Until then, use the sidebar or browser back/forward. Send us a note if there's a specific shortcut you need."),
      s.related(["overview"]),
    ],
  },

  // ─── DEALS ────────────────────────────────────────────────
  "deal-radar": {
    slug: "deal-radar",
    title: "Deal Radar",
    subtitle: "Iris AI's daily top-8 opportunities",
    icon: "Radar",
    read_time_min: 5,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Deal Radar merges every active listing (Bayut + PropertyFinder) with every open Emirates Auction, scores each on ~8 factors, and shows you the 8 hottest opportunities that morning. You can personalize what you see with **Deal Preferences** and compare 2 deals side-by-side."),
      s.h2("How the score is calculated"),
      s.p("Every 6 hours, Iris AI (Claude Sonnet 4.5) reads three signals for each candidate and produces a 0-100 **hotness score**:"),
      s.bullets([
        "**Discount vs sold comparables** — how far below the trailing 90-day sold AED/psf in the same community",
        "**Deal urgency** — hours to auction close, or days since listing",
        "**Risk exposure** — inspection window, agent verification, off-plan delivery risk, refurb condition",
        "**Community momentum** — sold-volume trend, active-inventory tightness, price direction",
        "**Refurb / capex outlook** — condition tags, off-plan handover date, service charge red-flags",
        "**Financing fit** — LTV feasibility given your recent tickets (learned from your workspace)",
        "**Exit ARV outlook** — projected 12-month AED/psf",
        "**Confidence** — how many comparable transactions the score is grounded on",
      ]),
      s.h2("Where you'll see Deal Radar"),
      s.fieldTable("Two Deal Radar surfaces", [
        { name: "**/app/trends** — Global radar", meaning: "The full 8-card view, source chips (all/Bayut/PropertyFinder), Compare 2, Preferences, Explainer, PDF export." },
        { name: "**/app/dashboard** — Personal radar", meaning: "Your top-5 personalized deals (mixed listings + auctions), sorted so preference-matched deals come first." },
      ]),
      s.h2("Card anatomy"),
      s.fieldTable("What each element on a Deal Radar card means", [
        { name: "Hotness (0-100)", meaning: "Higher = better. 85+ is a rare 'act now'; 70-84 is worth a viewing; <70 skip." },
        { name: "AED/psf", meaning: "Asking price ÷ built-up sqft. Compare against community stats below." },
        { name: "Green 'Verified' badge", meaning: "Bayut has cross-checked the agent + listing." },
        { name: "Amber 'Off-plan' badge", meaning: "Property not yet handed over — pay attention to completion date." },
        { name: "PF badge (orange)", meaning: "Sourced from PropertyFinder (via RapidAPI)." },
        { name: "Bayut badge (green)", meaning: "Sourced from Bayut (via RapidAPI)." },
        { name: "Iris **Matches you** badge", meaning: "This deal fits your saved Deal Preferences (budget, community, type, beds)." },
        { name: "**Outside prefs** badge", meaning: "Doesn't match your preferences — hover to see why." },
      ]),
      s.h2('Try "Why this score?"'),
      s.p("Every card has a **Why this score?** button that opens a full-screen Iris AI explainer with score breakdown bars, positives, risks, and up to 3 real comparable transactions. Export as branded PDF or share via WhatsApp."),
      s.tryLive("/app/trends", "Open Deal Radar"),
      s.h2("Compare 2 deals side-by-side"),
      s.p("Click **Compare 2** in the top-right, pick two cards (a green ring appears), then click **Compare with Iris AI** for a merged verdict: winner + confidence + 5-8 factor comparison table + pros/cons + suggested next steps."),
      s.tryLive("/app/trends", "Try Compare 2"),
      s.faq([
        { q: "How often does the Deal Radar refresh?", a: "Auto every 6 hours. Click 'Refresh' to force a re-score using cached data (fast) or trigger a new sync." },
        { q: "Why do I see the same deal from both Bayut and PropertyFinder?", a: "Iris fingerprints listings by community + building + beds + size-bucket + price-bucket. Same-fingerprint twins collapse in the default view. Toggle 'All sources' vs single-source chips to see them separately." },
        { q: "Can I exclude auctions?", a: "Yes — set **Deal type = Sale only** in your Deal Preferences. Auctions will then be badged 'Outside prefs' in the radar." },
      ]),
      s.related(["deal-preferences", "compare-2-deals", "live-listings", "deal-analyzer", "auctions", "hotspots"]),
    ],
  },

  "deal-preferences": {
    slug: "deal-preferences",
    title: "Deal Preferences (personalization)",
    subtitle: "Tell Iris your budget, communities and property criteria",
    icon: "Sliders",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Preferences are your per-user investment filter. Once set, Iris nudges Deal Radar (both the personal one on your dashboard and the global one on Trends) toward matches — while still showing everything so you never miss an opportunity."),
      s.h2("Open the preferences panel"),
      s.numbered([
        "Go to **/app/trends**",
        "In the Deal Radar toolbar (top-right), click **Preferences** (⚙️ icon)",
        "Set your budget, communities, property types, bedrooms, deal type and off-plan tolerance",
        "Click **Save preferences**",
      ]),
      s.h2("What you can filter on"),
      s.fieldTable("Six preference dimensions", [
        { name: "Budget (min/max AED)", meaning: "Cards with asking price outside this range get badged 'Outside prefs'." },
        { name: "Communities", meaning: "Multi-select from Dubai's top 30. Empty = all communities." },
        { name: "Property types", meaning: "apartment · villa · townhouse · penthouse · studio · plot · office. Empty = all." },
        { name: "Bedrooms", meaning: "Studio · 1 · 2 · 3 · 4 · 5+. Empty = all." },
        { name: "Deal type", meaning: "Both / Sale only / Auction only." },
        { name: "Off-plan tolerance", meaning: "OK / ≤12 months to handover / Ready only." },
      ]),
      s.h2("How Iris uses your preferences"),
      s.bullets([
        "**Global Deal Radar (/app/trends)** — every card gets a match/outside badge. A filter toggle lets you switch between **All deals** and **Matches only**.",
        "**Personal Deal Radar (/app/dashboard)** — matched deals bubble to the top of your 5-card feed.",
        "**Hot Listings & Hotspots** — soft nudge (not a hard filter) toward matching communities.",
      ]),
      s.callout("info", "You'll always see everything by default. Preferences never hide inventory — they just badge it. Only the **Matches only** toggle applies a hard filter, and Iris always offers a 'Show all deals' escape hatch if nothing matches."),
      s.tryLive("/app/trends", "Open Deal Radar & set preferences"),
      s.faq([
        { q: "Do preferences sync across devices?", a: "Yes — they're saved per-user in your Iris profile, not in the browser." },
        { q: "Can I share preferences with a teammate?", a: "Not yet — each user has their own set. On the roadmap." },
        { q: "Does 'Compare 2 Deals' respect preferences?", a: "The AI verdict does — Iris mentions how each deal aligns with your criteria." },
      ]),
      s.related(["deal-radar", "compare-2-deals"]),
    ],
  },

  "live-listings": {
    slug: "live-listings",
    title: "Live Listings",
    subtitle: "Dubai's active inventory — Bayut + PropertyFinder Residential + PropertyFinder Commercial",
    icon: "Globe2",
    read_time_min: 4,
    updated_at: "2026-02-21",
    sections: [
      s.summary("Live Listings shows every for-sale AND for-rent property currently on Bayut and PropertyFinder — both residential and commercial. Data refreshes on a schedule via RapidAPI, or on-demand via the three 'Sync' buttons."),
      s.h2("Sources"),
      s.fieldTable("Data providers", [
        { name: "Bayut", meaning: "Bayut API v3 (~10K listings per Dubai sync). Auto-sync every 6h. Residential only." },
        { name: "PropertyFinder Residential", meaning: "property-finder-api1 (RapidAPI). Auto-sync every 6h + 45m offset." },
        { name: "PropertyFinder Commercial (NEW)", meaning: "uae-real-estate-api-propertyfinder-ae-data (RapidAPI). Offices, retail, warehouses, showrooms, floors, buildings, factories, coworking. Auto-sync every 12h." },
      ]),
      s.h2("Filters that matter"),
      s.bullets([
        "**Segment** — All / Residential / Commercial. Commercial swaps the property-type dropdown to office / warehouse / retail / showroom / floor / building.",
        "**Type** — For sale / For rent (rent covers commercial leases too).",
        "**Community chips** — multi-select. Iris tracks 300+ Dubai communities.",
        "**Price / sqft** — sliders for asking price range and built-up size.",
        "**Property type** — apartment, villa, townhouse, penthouse (residential) OR office, shop, warehouse, showroom (commercial).",
        "**Completion status** — ready vs off-plan.",
        "**Source** — All / Bayut only / PF Residential / PF Commercial.",
        "**Sort** — newest listing, cheapest, cheapest AED/psf, most expensive.",
      ]),
      s.h2("Analyze any listing with Iris on-demand"),
      s.p("Every card has an **Analyze with Iris** button. Click it and Iris (Claude Sonnet 4.5) runs the same full deal-scoring workflow used in Deal Radar: hotness score + 8-factor breakdown + narrative + comparables + risks + PDF export. Works for residential AND commercial."),
      s.callout("info", "The first analysis of a listing takes ~30-60 seconds. Subsequent opens are INSTANT (cached 24 hours in `ai_insight_cache`). Click 'Refresh' inside the explainer modal to force a fresh analysis."),
      s.h2("Deduplication"),
      s.p("A single flip listed on both Bayut and PropertyFinder collapses into ONE row in the default view. Iris uses a fingerprint of `community | building | property_type | beds | size_bucket(25 sqft) | price_bucket(25k AED)` — buckets absorb the small price/size differences agents introduce between listing sites."),
      s.callout("info", "The response includes a `collapsed_twins` count showing how many duplicates were merged. This appears in the header when you're viewing the union."),
      s.tryLive("/app/market/live", "Open Live Listings"),
      s.faq([
        { q: "Why don't I see any PropertyFinder Commercial listings?", a: "Ensure PROPERTYFINDER_COMMERCIAL_API_KEY (or PROPERTYFINDER_API_KEY) is set in backend .env, then switch Segment → Commercial and click 'Sync Commercial'. First sync pulls ~300-800 offices/retail/warehouses across 10 Dubai hubs in 2-4 min." },
        { q: "How is a listing marked off-market?", a: "A nightly sweeper (3:15 AM Dubai) marks any listing not seen in the last 48h of syncs as `off_market=true`. It stays in the archive but is hidden from the default view." },
        { q: "Does the AI cache expire?", a: "Yes — the 24-hour cache is per-workspace, per-listing. After 24h the next click triggers a fresh Iris run so the analysis stays current with newest market data." },
      ]),
      s.related(["deal-radar", "trends", "hotspots"]),
    ],
  },

  "compare-2-deals": {
    slug: "compare-2-deals",
    title: "Compare 2 Deals",
    subtitle: "AI-generated side-by-side verdicts",
    icon: "Scale",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("When you're deciding between two opportunities, click **Compare 2** in the Deal Radar toolbar. Iris AI merges both items with fresh market context and produces a rigorous side-by-side verdict."),
      s.h2("How it works"),
      s.numbered([
        "On the Deal Radar section, click **Compare 2** in the top-right.",
        "The button flips into selection mode. Two cards can be selected — a green ring appears on each pick, and the counter shows `N/2 selected`.",
        "Click **Compare with Iris AI**.",
        "The comparison modal opens with: item A + B cards, verdict banner (green = A wins, blue = B wins, slate = tie), confidence %, 5-8 factor comparison table with per-row advantage badges, and pros/cons columns.",
      ]),
      s.h2("What Iris compares"),
      s.bullets([
        "**Discount vs sold psf** — computed live against the community's trailing 90-day sold AED/psf",
        "**Deal urgency** — auction hours-to-close vs days-since-listing",
        "**Risk exposure** — inspection window, verification, off-plan delivery",
        "**Refurb capex outlook** — condition tags, community-typical refurb spend",
        "**Expected exit ARV** — post-refurb 12-month projection",
      ]),
      s.callout("info", "Comparisons are cached for 12 hours (keyed by the sorted pair of item IDs) so re-opening the same pair is instant."),
      s.tryLive("/app/trends", "Open Deal Radar"),
      s.faq([
        { q: "Can I compare a listing against an auction?", a: "Yes — the compare endpoint accepts a mixed payload `[{source:'listing'|'auction', source_id: ...}, ...]`." },
        { q: "Does the comparison export to PDF?", a: "Not yet — currently only the single-deal Explainer exports as branded PDF. Comparison PDF export is on the backlog." },
      ]),
      s.related(["deal-radar", "deal-analyzer", "live-listings"]),
    ],
  },

  "deal-analyzer": {
    slug: "deal-analyzer",
    title: "Deal Analyzer",
    subtitle: "Full underwriting for a specific property",
    icon: "Calculator",
    read_time_min: 5,
    updated_at: "2026-02-20",
    sections: [
      s.summary("The Deal Analyzer is your deep-dive underwriting tool. Paste a Bayut/PropertyFinder URL or enter details manually. Iris AI returns fair market value, projected ARV, refurb budget, and a full flip P&L."),
      s.h2("Inputs"),
      s.bullets([
        "**URL** (optional) — Iris scrapes the listing and pre-fills community, building, size, price.",
        "**Manual override** — asking price, bedrooms, size, community, condition.",
        "**Refurb scope** — light / standard / premium / full-strip; Iris multiplies by community-typical AED/sqft.",
        "**Financing** — LTV %, interest rate, holding months.",
      ]),
      s.h2("Outputs"),
      s.fieldTable("What Iris returns", [
        { name: "Fair market price", meaning: "Comparable-driven fair value with confidence band." },
        { name: "ARV (After-Refurb Value)", meaning: "12-month exit price projection using recent refurbed sales in the community." },
        { name: "Refurb budget", meaning: "Iris's estimate broken by trade (MEP, tiling, joinery, paint, kitchen, bathroom, external)." },
        { name: "Holding costs", meaning: "Financing interest + service charges + insurance + agent fees for the projected timeline." },
        { name: "Flip P&L", meaning: "Purchase + refurb + holding + selling costs vs projected exit → net profit + IRR + cash-on-cash." },
        { name: "Sources", meaning: "Every number cites the underlying DLD/Bayut transactions or Iris AI reasoning." },
      ]),
      s.tryLive("/app/deal-analyzer", "Open Deal Analyzer"),
      s.related(["deal-radar", "compare-2-deals", "trends"]),
    ],
  },

  auctions: {
    slug: "auctions",
    title: "Auctions",
    subtitle: "Emirates Auction tracker",
    icon: "Gavel",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris pulls every active Emirates Auction lot into your workspace so you can shortlist, set reminders and jump straight to the Deal Analyzer with pre-populated numbers."),
      s.h2("What's tracked"),
      s.bullets([
        "Lot title, address, property type, bedrooms, size",
        "Starting bid + current bid + bid count",
        "Auction end time (with live hours-to-close counter)",
        "Photos + Emirates Auction listing URL",
        "Iris hotness score once the lot is >12 hours away from close",
      ]),
      s.h2("Reminders"),
      s.p("An hourly APScheduler cron scans open auctions and creates Task notifications for anything closing in the next 6 hours that matches your saved watchlist criteria (community, ticket, bedrooms)."),
      s.tryLive("/app/auctions", "Open Auctions"),
      s.related(["deal-radar", "deal-analyzer"]),
    ],
  },

  // ─── PORTFOLIO ────────────────────────────────────────────
  properties: {
    slug: "properties",
    title: "Properties (Asset Register)",
    subtitle: "Track every property in your portfolio",
    icon: "Building2",
    read_time_min: 5,
    updated_at: "2026-02-20",
    sections: [
      s.summary("The Asset Register at **/app/properties** is your source of truth for every property. Each moves through six stages: Sourcing → Under Offer → Owned → Refurb → Listed → Sold."),
      s.fieldTable("Property stages", [
        { name: "sourcing", meaning: "Iris AI flagged it; you're evaluating." },
        { name: "under_offer", meaning: "Offer submitted / MOU signed." },
        { name: "owned", meaning: "Closed. Title deed transferred." },
        { name: "refurb", meaning: "Active refurb — BOQs, POs, snags, drawings all live here." },
        { name: "listed", meaning: "Refurb complete, on-market for exit." },
        { name: "sold", meaning: "Sale closed. Full P&L calculated." },
      ]),
      s.h2("Property detail — 6 tabs"),
      s.bullets([
        "**Overview** — hero photo, community, size, purchase/list prices, running P&L.",
        "**BOQ & Vendors** — every quote for this property + Upload BOQ button.",
        "**Purchase Orders** — committed spend by vendor.",
        "**Snags & Punch List** — site issues.",
        "**Drawings & Permits** — attached PDFs.",
        "**Sales** — offers, viewings, marketing status.",
      ]),
      s.tryLive("/app/properties", "Open Assets"),
      s.related(["boq-upload", "boq-audit", "vendors", "refurb"]),
    ],
  },

  "boq-upload": {
    slug: "boq-upload",
    title: "BOQ Upload + Iris AI normalization",
    subtitle: "Ingest quotes in seconds, not hours",
    icon: "Upload",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Vendors send BOQs as PDFs, Excel spreadsheets or CSVs. Iris AI extracts every line item, categorizes it (MEP / Tiling / Joinery / …), computes totals, and optionally runs a fraud audit — all in ~30-90 seconds."),
      s.h2("Supported formats"),
      s.bullets([
        "**PDF** — searchable PDFs work best. Scanned/image-only PDFs return HTTP 422 — re-export the source Excel.",
        "**Excel** — `.xlsx` and `.xls`. Multi-sheet workbooks are stitched together.",
        "**CSV** — UTF-8 preferred. First row is treated as headers.",
      ]),
      s.h2("How to upload"),
      s.numbered([
        "Open a property at **/app/properties/:id**",
        "Click the **BOQ & Vendors** tab",
        "Click **Upload BOQ** (top-right)",
        "Pick the vendor, add an optional title, drop the file",
        "Toggle **Run Iris fraud audit** if you want an immediate risk score",
        "Click **Extract with Iris AI** — wait 30-90 seconds",
      ]),
      s.h2("What Iris does under the hood"),
      s.numbered([
        "Persists the source file to object storage so you can reference it later.",
        "Extracts raw text (pypdf) or rows (openpyxl) from the file.",
        "Sends the payload to Claude Sonnet 4.5 with a strict system prompt for real-estate BOQ normalization.",
        "Parses the JSON response into typed line items: category, description, unit, quantity, unit_rate_aed, total_aed, ai_flag.",
        "Creates a BOQ + BOQLine records linked to the property + vendor.",
        "Optionally runs the fraud audit → sets fraud_score + flags + recommendations.",
      ]),
      s.callout("info", "AI flags per line: `inflated_rate`, `quantity_mismatch`, `duplicate`, `ambiguous`. Review flagged rows before approving."),
      s.tryLive("/app/properties", "Start from a property"),
      s.faq([
        { q: "What if the file is image-only?", a: "The endpoint returns HTTP 422. Iris can't OCR scanned images (yet). Ask the vendor to re-export as a native PDF or Excel." },
        { q: "How big can the file be?", a: "25 MB max. If yours is bigger, split the workbook by trade." },
        { q: "Can I re-run the audit later?", a: "Yes — from the BOQ detail page, click 'Re-run audit' after editing quantities or rates." },
      ]),
      s.related(["boq-audit", "vendors", "properties"]),
    ],
  },

  "boq-audit": {
    slug: "boq-audit",
    title: "Iris BOQ fraud audit",
    subtitle: "AI-flagged line items before you sign",
    icon: "ShieldAlert",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Every BOQ can be audited by Iris AI. It cross-checks each line against community-typical rates and portfolio history, then returns a 0-100 risk score plus specific line-level flags."),
      s.fieldTable("Risk score bands", [
        { name: "0-29 (green)", meaning: "Looks fair — proceed with normal review." },
        { name: "30-59 (amber)", meaning: "Some concerns — verify highlighted lines with the vendor." },
        { name: "60-100 (red)", meaning: "High risk — Iris found multiple inflated or duplicated items. Get a second quote." },
      ]),
      s.h2("Flag types"),
      s.bullets([
        "**inflated_rate** — unit rate >30% above community/portfolio median for this trade.",
        "**quantity_mismatch** — quantity seems inconsistent with property size or scope.",
        "**duplicate** — same work billed under two different line items.",
        "**ambiguous** — description is too vague to price ('site works', 'other').",
      ]),
      s.tryLive("/app/properties", "Audit a BOQ in a property"),
      s.related(["boq-upload", "vendors"]),
    ],
  },

  vendors: {
    slug: "vendors",
    title: "Vendor management",
    subtitle: "Directory + scorecards + portal accounts",
    icon: "Users",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("At **/app/vendors** you can add vendors, view their performance scorecard, and create a **portal admin account** so they can log in and manage their own team."),
      s.h2("Add a vendor"),
      s.numbered([
        'Click "+ Add vendor"',
        "Fill company, trade, contact person, **email (required)** and phone",
        "Save — the vendor appears in your directory",
      ]),
      s.h2("Vendor scorecard"),
      s.p("Every vendor is graded A / B / C / D on 5 factors: on-time delivery, budget adherence, BOQ accuracy (via Iris audit history), snag rate and payment cycle. Grades refresh nightly from the vendor's linked POs, invoices and snags."),
      s.h2("Create the vendor's portal login"),
      s.numbered([
        "On the vendor card, click **Create portal admin**",
        "Confirm the email",
        "Iris generates a one-time temp password — copy it, send via WhatsApp/email",
        "Vendor logs in at **/vendor-portal**, is forced to change password on first login",
        "From there, the vendor admin can invite their own team — see the Vendor Portal → Team article",
      ]),
      s.callout("info", "The vendor card now shows a live counter: `N portal users` with an Active status pill once the vendor has at least one active login."),
      s.tryLive("/app/vendors", "Open Vendors"),
      s.related(["vendor-portal-intro", "vendor-team-management", "boq-upload"]),
    ],
  },

  refurb: {
    slug: "refurb",
    title: "Refurb Management",
    subtitle: "BOQs, POs, snags in one place per property",
    icon: "Hammer",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Every property in `refurb` stage becomes a mini-project: BOQs → Approved POs → Invoices → Snags → Completion. All views live under the property detail page tabs."),
      s.bullets([
        "**BOQ tab** — quotes from vendors + Iris audit history",
        "**POs tab** — approved commitments; blocks over-commitment",
        "**Snags tab** — punch list with photos, severity, vendor assignment",
        "**Drawings tab** — permits, plans, MEP diagrams",
      ]),
      s.tryLive("/app/properties", "Open a property in Refurb"),
      s.related(["boq-upload", "purchase-orders", "properties"]),
    ],
  },

  "purchase-orders": {
    slug: "purchase-orders",
    title: "Purchase Orders",
    subtitle: "Turn approved BOQ lines into committed spend",
    icon: "ClipboardCheck",
    read_time_min: 2,
    updated_at: "2026-02-20",
    sections: [
      s.summary("A Purchase Order (PO) commits spend against a vendor for a subset of BOQ lines. Iris blocks over-commitment beyond the property's total refurb budget and warns if a PO exceeds the vendor's typical ticket."),
      s.h2("Approval workflow"),
      s.numbered([
        "PM raises a PO from approved BOQ lines",
        "Owner/MD approves at **/app/approvals**",
        "Vendor sees the PO in their portal (if permission granted)",
        "Vendor uploads invoices against the PO",
      ]),
      s.tryLive("/app/approvals", "Open Approvals"),
      s.related(["boq-upload", "invoices", "approvals-workflow"]),
    ],
  },

  // ─── FINANCE ──────────────────────────────────────────────
  invoices: {
    slug: "invoices",
    title: "Invoices",
    subtitle: "From draft to paid",
    icon: "Receipt",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Invoices sit at the intersection of a vendor + a property + an (optional) PO. Every invoice runs through: draft → submitted → approved → paid → reconciled."),
      s.fieldTable("Invoice status", [
        { name: "draft", meaning: "Vendor uploaded, not yet submitted for review." },
        { name: "submitted", meaning: "Awaiting internal approval." },
        { name: "approved", meaning: "Ready for payment; blocks further edits." },
        { name: "paid", meaning: "Payment released; net_payable_aed = amount_aed – retentions." },
        { name: "reconciled", meaning: "Bank statement matched." },
        { name: "rejected", meaning: "Sent back to vendor with notes." },
      ]),
      s.tryLive("/app/accounting", "Open Accounting"),
      s.related(["accounting", "approvals-workflow"]),
    ],
  },

  accounting: {
    slug: "accounting",
    title: "Accounting",
    subtitle: "Cash flow, aging, retentions",
    icon: "Wallet",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("The Accounting page is your finance cockpit: outstanding invoices by aging bucket, retention amounts held per vendor, real-time cash flow forecast, and DEWA/AGENT/service-charge one-off costs per property."),
      s.tryLive("/app/accounting", "Open Accounting"),
      s.related(["invoices", "approvals-workflow"]),
    ],
  },

  "approvals-workflow": {
    slug: "approvals-workflow",
    title: "Approvals workflow",
    subtitle: "Who signs off on what",
    icon: "ClipboardCheck",
    read_time_min: 2,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Any spend commitment (PO) or payment release needs approval. The Approvals inbox at **/app/approvals** shows everything waiting on you, filtered by role."),
      s.tryLive("/app/approvals", "Open Approvals"),
      s.related(["purchase-orders", "invoices"]),
    ],
  },

  // ─── MARKET INTEL ────────────────────────────────────────
  trends: {
    slug: "trends",
    title: "Market Trends",
    subtitle: "Sold psf, volumes and momentum",
    icon: "BarChart3",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("The Trends page shows Dubai's transaction history + live inventory + Iris AI hotspots on one dashboard. Data is grounded on ~200K DLD records that Iris pulls incrementally every 12 hours from Bayut API v3."),
      s.h2("What you'll find"),
      s.bullets([
        "**Community leaderboard** — top movers by sold volume, avg psf, and 90d price direction",
        "**Sold vs Rent psf charts** — 24-month trailing series",
        "**Deal Radar** — Iris's daily top 8 opportunities",
        "**Iris AI Hotspots** — the 8 communities where the ratio of great deals to average deals is highest right now",
        "**Live Listings** — active inventory (Bayut + PropertyFinder)",
      ]),
      s.tryLive("/app/market", "Open Trends"),
      s.related(["hotspots", "dld-data", "live-listings"]),
    ],
  },

  hotspots: {
    slug: "hotspots",
    title: "Iris AI Hotspots",
    subtitle: "Where deals are hiding",
    icon: "Flame",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Hotspots are the communities where Iris AI's ratio of high-hotness deals to total available deals is highest right now. Refreshed every 6 hours."),
      s.h2("Card anatomy"),
      s.fieldTable("Hotspot card", [
        { name: "Score", meaning: "0-100 — orange >85 = act now, amber 70-84 = look this week, slate <70 = watch." },
        { name: "Trend", meaning: "COOLING / STABLE / HEATING / SURGING — based on sold-volume + psf-direction over 90d." },
        { name: "Community", meaning: "The Dubai community (Marina, JVC, Business Bay, etc.)" },
        { name: "Headline", meaning: "Iris's one-line summary." },
        { name: "Reasoning (Read more)", meaning: "Full 3-5 sentence Iris rationale — click Read more to expand." },
        { name: "Ticket + type", meaning: "Iris's suggested ideal ticket size + property type for this community right now." },
      ]),
      s.tryLive("/app/market", "Open Hotspots"),
      s.related(["deal-radar", "trends"]),
    ],
  },

  "dld-data": {
    slug: "dld-data",
    title: "DLD Historical Data",
    subtitle: "Every Dubai transaction, cumulatively archived",
    icon: "Database",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris maintains a permanent local archive of Dubai Land Department transactions. Bayut API v3 caps each query at 10K rows, so we run scheduled deltas every 12 hours and never wipe the collection — the archive grows cumulatively past 200K rows."),
      s.h2("What's captured per transaction"),
      s.bullets([
        "DLD ID, transaction date, transaction type (sale / rent)",
        "Community, sub-community, building",
        "Property type, bedrooms, size (sqft)",
        "Price (AED), AED per sqft",
        "Off-plan flag",
        "Source: `bayutapi_v3`",
      ]),
      s.callout("warning", "Never wipe the `market_transactions` collection during testing — it acts as the cumulative historical archive and would take weeks to rebuild."),
      s.related(["trends", "hotspots", "daily-digest"]),
    ],
  },

  "daily-digest": {
    slug: "daily-digest",
    title: "Morning Brief (Daily Digest)",
    subtitle: "Yesterday in Dubai property, summarized every morning",
    icon: "Sunrise",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Every morning at 06:00 Dubai time, Iris AI compiles a Morning Brief of yesterday's market activity — new listings, closed transactions, community leaderboards, AI verdicts on which sales were steals or overpriced, and auctions closing in the next 24 hours."),
      s.h2("What's in the brief"),
      s.bullets([
        "**Market pulse** — new listings + closed transactions + total AED transacted + avg AED/psf vs 30-day baseline + hottest community",
        "**Iris AI verdicts** — top 3 steals (>15% below community 90d median psf) and top 3 overpriced (>15% above), each with a plain-English reason",
        "**Yesterday's most active communities** — 8-row leaderboard by deal volume + value + median psf",
        "**Current Deal Radar** — 6-card snapshot of what Iris is watching today",
        "**Auctions closing in 24h** — sorted by urgency; red badge if <4h remaining",
      ]),
      s.h2("How to access it"),
      s.numbered([
        "Left sidebar → **Market Intelligence** → **Morning Brief**",
        "Or go direct: **/app/daily-digest**",
      ]),
      s.h2("Rebuild manually"),
      s.p("Owner/MD can force a rebuild any time via the **Rebuild** button in the top-right of the digest page. Handy when a fresh sync just landed and you want the digest to reflect it."),
      s.tryLive("/app/daily-digest", "Open Morning Brief"),
      s.faq([
        { q: "Why don't I see many transactions yesterday?", a: "DLD data flows into Iris ~12-24h after each transaction closes. Give the sync a day and it'll fill in." },
        { q: "What if no steals or overpriced sales are found?", a: "Iris only flags sales that deviate >15% from the community's 90-day median psf. On a calm day, this list is empty — that's normal." },
        { q: "Will the digest include UAE real-estate news?", a: "Phase 2. We'll wire an RSS/Perplexity feed once the current features stabilize." },
      ]),
      s.related(["trends", "hotspots", "deal-radar", "dld-data"]),
    ],
  },

  "rental-leaderboard": {
    slug: "rental-leaderboard",
    title: "Rental Hold Leaderboard",
    subtitle: "Dubai's top-yielding communities RIGHT NOW — an Iris original",
    icon: "Trophy",
    read_time_min: 3,
    updated_at: "2026-02-21",
    sections: [
      s.summary("The Rental Hold Leaderboard ranks Dubai communities by GROSS RENTAL YIELD % using live asking rents and live asking sale prices — refreshed every 6h. No other Dubai portal does this. Property Monitor and REIDIN show yields based on DLD-registered rents (90-day delay); Iris shows what tenants are asking TODAY."),
      s.h2("How the yield is calculated"),
      s.p("For each community with ≥5 live rent listings AND ≥2 sale samples:"),
      s.bullets([
        "**Median rent psf** = median of `asking_aed_per_sqft` across all active rent listings in that community.",
        "**Median sale psf** = median of `asking_aed_per_sqft` across active sale listings + DLD-sold psf (last 90 days) combined.",
        "**Gross yield %** = median rent psf ÷ median sale psf × 100.",
        "Yields >20% are treated as data anomalies (usually monthly-vs-annual confusion) and clipped.",
      ]),
      s.h2("Yield bands"),
      s.fieldTable("What the color band means", [
        { name: "Exceptional (≥9%)",  meaning: "Truly rare in mature Dubai. Investigate — could be commercial or off-plan discount." },
        { name: "High (7.5-8.9%)",    meaning: "Strong hold thesis; cash-flow investors should pounce." },
        { name: "Healthy (6.0-7.4%)", meaning: "Solid; blend flip and hold — either exit is viable." },
        { name: "Moderate (4.5-5.9%)",meaning: "Standard Dubai residential yield. Rely more on capital appreciation." },
        { name: "Low (3.0-4.4%)",     meaning: "Yield below AED financing cost. Only worth it if flip discount is deep." },
        { name: "Weak (<3.0%)",       meaning: "Overpriced trophy assets. Skip unless you're buying for lifestyle." },
      ]),
      s.h2("Filters"),
      s.bullets([
        "**Property type** — Apartment / Villa / Studio / Office / Warehouse etc. Yield differs dramatically by asset class (offices in DIFC ~9%, villas in Emirates Hills ~3%).",
        "**Bedrooms** — Studios and 1BRs typically yield 2-3 pts higher than 3-4BR homes.",
        "**Minimum listings** — raise this filter for higher confidence in the ranking (default 5 is a good balance).",
        "**Top N** — 10, 20, or 50 communities.",
      ]),
      s.h2("Drill-through actions"),
      s.p("Click any row to expand and get 3 drill-through buttons: **See rentals** (opens Live Listings pre-filtered), **See for-sale**, **Community trends** (opens Trends page for that community). Instant path from macro insight to individual listing."),
      s.tryLive("/app/market/leaderboard", "Open Rental Leaderboard"),
      s.faq([
        { q: "Why don't I see my favorite community?", a: "It needs ≥5 live rent listings. Lower the 'min live rents' filter to 3 to see thinner markets, or wait for the next Bayut sync (every 6h)." },
        { q: "Is this the same as gross yield from Property Monitor?", a: "Similar formula but our data is FRESHER. PM uses DLD sold + DLD registered rent (both delayed 30-90 days). We use live asking prices — reflects what landlords are trying to charge THIS week." },
        { q: "Does yield include service charges?", a: "No — this is GROSS yield. Deduct 15-25% for service charges + agent commission + vacancy to get net yield." },
      ]),
      s.related(["live-listings", "trends", "deal-radar"]),
    ],
  },


  // ─── VENDOR PORTAL ────────────────────────────────────────
  "vendor-portal-intro": {
    slug: "vendor-portal-intro",
    title: "Vendor Portal — for external vendors",
    subtitle: "Log in, see your work, upload invoices",
    icon: "LogIn",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Vendors do not access your main Iris workspace. They log in at **/vendor-portal** with a dedicated email + password. Their session is a 7-day cookie, no 2FA — Iris scopes their data server-side to only what belongs to their vendor_id."),
      s.h2("How a vendor gets access"),
      s.numbered([
        "Owner or MD creates the vendor's **portal admin** account from **/app/vendors** (see Vendor Management)",
        "The vendor receives a temp password (share via WhatsApp/email)",
        "Vendor visits **/vendor-portal**, logs in, changes password on first login",
        "The vendor admin then invites their own accounts / contracting / site teams — see Vendor Portal → Team Management",
      ]),
      s.h2("What a vendor sees"),
      s.bullets([
        "**Overview** — cash paid + pending + open snags + active BOQs",
        "**Invoices** — every invoice they submitted",
        "**Snags** — open punch-list items assigned to them",
        "**BOQs** — every quote they've submitted",
        "**Properties** — only properties they're actively working on",
        "**Team** — self-service team management (admin only) or view-only for members",
      ]),
      s.tryLive("/vendor-portal", "Open Vendor Portal"),
      s.related(["vendor-team-management", "vendors"]),
    ],
  },

  "vendor-team-management": {
    slug: "vendor-team-management",
    title: "Vendor Portal — Team management (self-service)",
    subtitle: "Vendor admins invite their own team",
    icon: "UserPlus",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("One vendor company usually has multiple people — accounts, contracts, site foreman, PM. Iris supports **multi-user vendor accounts**: the primary vendor admin can invite, edit permissions, deactivate and remove team members from inside the portal, without needing to ask you."),
      s.h2("The Team tab"),
      s.p("After login, vendor admins see a new **Team** tab in the portal. Non-admins see the tab in view-only mode."),
      s.h2("Six granular permissions per team member"),
      s.fieldTable("What each permission gates", [
        { name: "View BOQs", meaning: "See approved quantities + unit rates. Off → BOQ endpoints return 403." },
        { name: "View Invoices", meaning: "See amounts owed and paid." },
        { name: "View Snags", meaning: "See the site punch list." },
        { name: "View Purchase Orders", meaning: "See committed spend." },
        { name: "View Files", meaning: "Access attached documents." },
        { name: "Manage team", meaning: "Adds them as a co-admin — can invite/edit/remove other members. Iris blocks demoting the last admin." },
      ]),
      s.h2("Invite a team member"),
      s.numbered([
        "Vendor admin logs in at /vendor-portal",
        "Clicks **Team** tab",
        "Clicks **Invite team member**",
        "Fills email + name + job title (e.g. 'Accounts Manager', 'Site Foreman')",
        "Ticks the permissions this person should have",
        "Clicks **Send invite** — Iris shows a temp password ready to copy",
        "Vendor admin shares the temp password with the team member via WhatsApp/email",
      ]),
      s.h2("Manage an existing member"),
      s.p("On the Team roster, click **Manage** on any row. From the modal you can:"),
      s.bullets([
        "Update name and job title",
        "Toggle any of the 6 permissions on/off",
        "**Reset password** — regenerates a temp password and terminates all their current sessions",
        "**Deactivate** — keeps the record but blocks logins",
        "**Remove** — soft-deletes them from the team (permanent)",
      ]),
      s.callout("warning", "Iris blocks the last active vendor admin from removing themselves or demoting themselves. Nominate a second admin first."),
      s.h2("Property scope inheritance"),
      s.p("When a vendor admin has been scoped to specific properties (via the Iris internal admin), any team member they invite will inherit that same scope. Sub-users cannot see MORE properties than the admin."),
      s.tryLive("/vendor-portal", "Open Vendor Portal"),
      s.faq([
        { q: "Can a member see who invited them?", a: "Yes — `invited_by_user_id` is stored on every sub-user record and visible on the roster." },
        { q: "How do I remove a whole vendor company?", a: "Iris internal admin deactivates the vendor record. All linked users lose portal access automatically." },
        { q: "Can multiple vendor admins exist at the same time?", a: "Yes — any team member with `manage_team=true` becomes a co-admin. Iris only enforces at least ONE active admin at all times." },
      ]),
      s.related(["vendor-portal-intro", "vendors", "roles-permissions"]),
    ],
  },

  // ─── AI ───────────────────────────────────────────────────
  "iris-ai-overview": {
    slug: "iris-ai-overview",
    title: "Iris AI — what powers the intelligence",
    subtitle: "Claude Sonnet 4.5 wired into every module",
    icon: "Sparkles",
    read_time_min: 4,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris AI is a bundle of Claude Sonnet 4.5-powered features accessible through the Emergent LLM key. Every AI feature is grounded on your workspace data + real DLD comparables — no hallucinated numbers."),
      s.h2("Where Iris shows up"),
      s.fieldTable("Iris features across the app", [
        { name: "Deal Radar", meaning: "Ranks and scores 8 deals daily with reasoning." },
        { name: "Why this score?", meaning: "Full narrative + score breakdown + comparables + PDF export." },
        { name: "Compare 2 Deals", meaning: "Merged verdict with factor table and pros/cons." },
        { name: "Deal Analyzer", meaning: "Fair value + ARV + refurb budget + flip P&L." },
        { name: "BOQ normalization", meaning: "Extracts and structures line items from PDF/Excel/CSV BOQs." },
        { name: "BOQ fraud audit", meaning: "Line-level flags + 0-100 risk score." },
        { name: "Hotspots", meaning: "Community-level ranking of best-value opportunities." },
      ]),
      s.h2("Grounding + accuracy"),
      s.p("Iris never invents comparables. Every deal-related prompt is prefixed with real market context pulled from `market_transactions` (DLD) + `market_listings` (Bayut + PropertyFinder) for the same community, property type and beds. If Iris doesn't have enough data, it returns a lower confidence score rather than a wild guess."),
      s.h2("Cost + rate limits"),
      s.p("Iris runs on the Emergent LLM key — a single key routes across OpenAI, Anthropic, and Google APIs. Balance and top-up settings are in your Emergent profile."),
      s.related(["deal-radar", "boq-upload", "hotspots"]),
    ],
  },

  // ─── SECURITY ─────────────────────────────────────────────
  "two-fa-setup": {
    slug: "two-fa-setup",
    title: "Two-Factor Authentication (2FA)",
    subtitle: "Workspace-enforced TOTP setup",
    icon: "ShieldCheck",
    read_time_min: 3,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris workspaces enforce 2FA for all internal users. On first login you're redirected to **/app/security** to enrol a TOTP authenticator (Google Authenticator, 1Password, Authy)."),
      s.h2("Setup steps"),
      s.numbered([
        "Sign in with your Google account",
        "Iris redirects to /app/security with an amber banner",
        "Scan the QR code in your authenticator app",
        "Enter the 6-digit code twice to confirm",
        "Save 5 backup codes somewhere safe",
      ]),
      s.callout("info", "External vendors do NOT need 2FA — they use email/password with a 7-day session cookie. This keeps their onboarding friction low."),
      s.tryLive("/app/security", "Open Security"),
      s.related(["roles-permissions"]),
    ],
  },

  "audit-log": {
    slug: "audit-log",
    title: "Audit Log",
    subtitle: "Every sensitive action, recorded",
    icon: "ScrollText",
    read_time_min: 2,
    updated_at: "2026-02-20",
    sections: [
      s.summary("Iris logs every sensitive action to the `audit_log` collection: user creation, role change, workspace switch, vendor account creation, PO approval, invoice payment, BOQ deletion. Full history at **/app/audit**."),
      s.tryLive("/app/audit", "Open Audit Log"),
      s.related(["roles-permissions"]),
    ],
  },
};

// Convenience: list of all article slugs in TOC order (for prev/next nav)
export const ARTICLE_ORDER = HELP_CATEGORIES.flatMap(c => c.slugs);
